import { useQueryParam, StringParam } from 'use-query-params'
import { useSessionStorage } from 'react-use'
import { useCart, utils } from '@chordcommerce/gatsby-theme-autonomy'
import { useEffect, useState } from 'react'

/**
 * @param {{codeString:string}}
 * Given the string promocode param value, do these things
 *
 * If cart is loading, promocode was already
 * evaluated in client side, or promocode is empty:
 *  -> Do not evaluate further.
 *
 * If cart is empty, save promocode in SessionStorage for future use.
 *
 * If promocode already exists in cart, return
 *
 * If promocode is used and either fails or succeeds, mark it
 *
 * Place this hook on a top level component.
 */

export default function useURLPromotion({ codeString = '' }) {
  const { cart, addPromoCode, isFetching } = useCart()
  const { getAllCartPromotionsForDisplay } = utils
  const [promocode] = useQueryParam(codeString, StringParam)
  const [usedData, setUsedData] = useState(false)
  const [storedPromoValue, setStoredPromoValue] =
    useSessionStorage('promocode-hold') // In case cart is unable to accept promo currently (because cart is empty), set promocode in session storage

  useEffect(() => {
    // Check if promocode and cart data is ready, return if promocode is already used
    if ((!promocode && !storedPromoValue) || isFetching || usedData) return

    // If cart lineitems is empty, save code for later use.
    if (!cart?.lineItems?.length) {
      if (!storedPromoValue?.length) setStoredPromoValue(promocode)
      return
    }

    const currentCode = promocode ?? storedPromoValue // Set code to use, give promocode param the priority

    // Check if cart already includes this promocode
    const promotions = getAllCartPromotionsForDisplay(cart.data)
    const promoExists = promotions.some(
      promo => promo?.code?.toLowerCase() === currentCode?.toLowerCase(),
    )
    if (promoExists) {
      setStoredPromoValue('')
      return
    } // In case of reload, check if promocode already exists.

    addPromoCode({ promoCode: currentCode })
      .catch(e => console.error(e))
      .finally(() => {
        setUsedData(true)
        setStoredPromoValue('')
      }) // Only apply once.
  }, [promocode, cart, usedData, storedPromoValue])
}
